import { FC, useCallback, useEffect, useMemo } from 'react'
import type { FileWithPath } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { useFormikContext } from 'formik'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import useTranslate from '@/hooks/use-translate'
import DialogUploadValues from '@/types/dialogUploadValues'
import { MAX_DOSSIER_NAME_LENGTH } from '@/constants/NameLengths'
import { filterFileTypes } from '@/utils'
import { NfFileRow } from '../primitives/NfFileRow'
import { NfSectionHeading } from '../primitives/NfSectionHeading'
import { getNf, nf } from '../nfTokens'

export interface FilesFieldProps {
  useZip?: boolean
}

/**
 * Étape « Fichiers » du wizard : zone de dépôt toujours visible, avec en
 * dessous la liste des fichiers ajoutés dès qu'il y en a.
 */
export const FilesField: FC<FilesFieldProps> = ({
  useZip,
}) => {
  const { t: tCommon } = useTranslate('common')
  const { t: tUpload } = useTranslate('upload')
  const formik = useFormikContext<DialogUploadValues>()

  const files = useMemo<FileWithPath[]>(
    () => formik.values.files as FileWithPath[],
    [formik.values.files]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: filterFileTypes({
      tCommon,
      callback: (filteredAcceptedFiles: File[]) => {
        formik.setFieldValue('files', files.concat(filteredAcceptedFiles))
      },
      useZip,
    }),
  })

  const removeFile = useCallback(
    (index: number) => {
      if (files.length > index) {
        const newFiles = [...files]
        newFiles.splice(index, 1)
        formik.setFieldValue('files', newFiles)
      }
    },
    [files]
  )

  useEffect(() => {
    if (files.length > 0 && !formik.values.recordName) {
      let base = files[0].name.split('.').slice(0, -1).join('.')
      if (base.length > MAX_DOSSIER_NAME_LENGTH) base = base.slice(0, MAX_DOSSIER_NAME_LENGTH)
      formik.setFieldValue('recordName', base, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files])

  const hasFiles = files.length > 0

  return (
    <Stack spacing={1.25} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <NfSectionHeading>{tUpload('wizardStepFilesLabel')}</NfSectionHeading>
      <Stack spacing={0.75} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box
        {...getRootProps()}
        data-testid="dropzone"
        sx={(theme) => {
          const t = getNf(theme)
          return {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.75,
            flex: '0 0 auto',
            minHeight: hasFiles ? undefined : 140,
            p: '34px 20px',
            cursor: 'pointer',
            textAlign: 'center',
            border: `1px dashed ${isDragActive ? t.green : t.dash}`,
            bgcolor: isDragActive ? t.greenTint : t.surfaceMuted,
            borderRadius: '12px',
            transition: 'background-color .14s, border-color .14s',
          }
        }}
      >
        <input
          {...getInputProps()}
          data-testid="dropzone-input"
          accept=".pdf, .jpeg, .jpg, .png, .xls, .xlsx, .ods"
        />
        <Box
          sx={(theme) => {
            const t = getNf(theme)
            return {
              width: 34,
              height: 34,
              borderRadius: `${nf.radiusCard}px`,
              bgcolor: t.surface,
              border: `1px solid ${t.line}`,
              display: 'grid',
              placeItems: 'center',
              color: 'primary.main',
            }
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 17 }} />
        </Box>
        <Typography sx={(theme) => ({ fontSize: 12, fontWeight: 600, color: getNf(theme).ink })}>
          {isDragActive ? tUpload('wizardDropTitleActive') : tUpload('wizardDropTitle')}
        </Typography>
        <Typography sx={(theme) => ({ fontSize: 10.5, color: getNf(theme).muted })}>
          {tUpload('wizardDropSubtitle')}
        </Typography>
      </Box>

      {hasFiles && (
        <Stack spacing={0.75} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          {files.map((file, index) => (
            <Box key={file.name + index} data-testid={`file-row-${index}`}>
              <NfFileRow
                name={file.name}
                size={file.size}
                onRemove={() => removeFile(index)}
              />
            </Box>
          ))}
        </Stack>
      )}
      </Stack>
    </Stack>
  )
}

export default FilesField
